import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { superadminApi, type EmailTemplateVariableDto } from '@/api/superadmin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, RotateCcw, Save, Eye, Code2, X } from 'lucide-react'

type Tab = 'editor' | 'preview'

// ── Inline confirm modal ───────────────────────────────────────────────────────
function ConfirmModal({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string
  message: string
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600 -mt-1">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-slate-600 mb-6">{message}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
          <Button
            size="sm"
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Variable chip ──────────────────────────────────────────────────────────────
function VariableChip({ v }: { v: EmailTemplateVariableDto }) {
  const [copied, setCopied] = useState(false)
  const handleClick = () => {
    navigator.clipboard.writeText(`{{${v.name}}}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <button
      onClick={handleClick}
      className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 transition-colors"
      title={`Click to copy {{${v.name}}}`}
    >
      <code className="text-xs font-mono text-indigo-600">
        {copied ? '✓ Copied!' : `{{${v.name}}}`}
      </code>
      <p className="text-xs text-slate-500 mt-0.5">{v.description}</p>
      <p className="text-xs text-slate-400 italic mt-0.5">e.g. {v.example}</p>
    </button>
  )
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function EmailTemplateEditorPage() {
  const { key } = useParams<{ key: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showConfirm, setShowConfirm] = useState(false)

  const { data: tmpl, isLoading } = useQuery({
    queryKey: ['superadmin', 'email-templates', key],
    queryFn: () => superadminApi.getEmailTemplate(key!).then(r => r.data),
    enabled: !!key,
  })

  const [subject, setSubject] = useState('')
  const [htmlBody, setHtmlBody] = useState('')
  const [activeTab, setActiveTab] = useState<Tab>('editor')

  useEffect(() => {
    if (tmpl) {
      setSubject(tmpl.subject)
      setHtmlBody(tmpl.htmlBody)
    }
  }, [tmpl])

  const isDirty = tmpl && (subject !== tmpl.subject || htmlBody !== tmpl.htmlBody)

  const saveMutation = useMutation({
    mutationFn: () => superadminApi.updateEmailTemplate(key!, { subject, htmlBody }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'email-templates'] })
    },
  })

  const resetMutation = useMutation({
    mutationFn: () => superadminApi.resetEmailTemplate(key!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'email-templates'] })
      navigate('/superadmin/email-templates')
    },
  })

  if (isLoading || !tmpl) {
    return <div className="py-12 text-center text-sm text-slate-400">Loading template…</div>
  }

  return (
    <div className="max-w-6xl">
      {showConfirm && (
        <ConfirmModal
          title="Reset to default?"
          message={`This will remove your customised content and revert "${tmpl.name}" to the factory default. This cannot be undone.`}
          confirmLabel="Reset to default"
          onConfirm={() => resetMutation.mutate()}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* Header */}
      <div className="flex items-start gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/superadmin/email-templates')}
          className="mt-0.5 -ml-2 text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900">{tmpl.name}</h1>
            {tmpl.isCustomised
              ? <Badge variant="default" className="bg-indigo-600 text-white text-xs">Custom</Badge>
              : <Badge variant="neutral" className="text-xs">Default</Badge>
            }
          </div>
          <p className="text-sm text-slate-500 mt-0.5">{tmpl.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {tmpl.isCustomised && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowConfirm(true)}
              className="gap-1.5 text-slate-600"
              disabled={resetMutation.isPending}
            >
              <RotateCcw size={14} />
              Reset to default
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending}
            className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Save size={14} />
            {saveMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-[1fr_260px] gap-6">
        {/* Left — editor */}
        <div className="space-y-4">
          {/* Subject */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">
              Subject line
            </Label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="font-mono text-sm"
              placeholder="Email subject…"
            />
          </div>

          {/* Body editor + preview tabs */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-1 px-4 py-2 border-b border-slate-100 bg-slate-50">
              <button
                onClick={() => setActiveTab('editor')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  activeTab === 'editor'
                    ? 'bg-white border border-slate-200 text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Code2 size={13} />
                HTML
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  activeTab === 'preview'
                    ? 'bg-white border border-slate-200 text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Eye size={13} />
                Preview
              </button>
              <span className="ml-auto text-xs text-slate-400">
                {htmlBody.length.toLocaleString()} chars
              </span>
            </div>

            {activeTab === 'editor' ? (
              <textarea
                value={htmlBody}
                onChange={e => setHtmlBody(e.target.value)}
                className="w-full h-[560px] px-4 py-3 font-mono text-xs text-slate-800 resize-none focus:outline-none bg-white"
                spellCheck={false}
                placeholder="<html>…"
              />
            ) : (
              <iframe
                key={htmlBody}
                srcDoc={htmlBody}
                className="w-full h-[560px] border-0 bg-white"
                sandbox="allow-same-origin"
                title="Email preview"
              />
            )}
          </div>

          {saveMutation.isSuccess && !isDirty && (
            <p className="text-sm text-green-600 font-medium">✓ Template saved successfully</p>
          )}
          {saveMutation.isError && (
            <p className="text-sm text-red-600">Failed to save. Please try again.</p>
          )}
        </div>

        {/* Right — variable reference */}
        <div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden sticky top-4">
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Available variables
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Click to copy token</p>
            </div>
            <div className="divide-y divide-slate-50 max-h-[600px] overflow-y-auto">
              {tmpl.availableVariables.length === 0 ? (
                <p className="px-4 py-3 text-xs text-slate-400 italic">
                  No variables for this template.
                </p>
              ) : (
                tmpl.availableVariables.map((v: EmailTemplateVariableDto) => (
                  <VariableChip key={v.name} v={v} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
